/**
 * RSFY-794 — variant-change bridge for the product-page 3D viewer app block.
 *
 * The PDP 3D viewer is delivered by a third-party app block whose code we
 * cannot modify (it is injected by Shopify from the app's extension). Its
 * model-swap handler binds exclusively to
 *
 *   $('body').on('click', '.product-form__item .single-option-selector', ...)
 *
 * which is Debut-era product-form markup. This theme renders the variant
 * picker as radios or selects inside <variant-selects> depending on
 * picker_type — neither of which matches that selector — so the app's
 * delegate never fires after its initial page-load read, and changing the
 * carat left the previous 3D model (and "pictured model" line) on screen
 * until a refresh.
 *
 * This bridge listens to the theme's own variant-change broadcast and
 * re-supplies the click the app is waiting for: a synthetic bubbling click
 * on a hidden decoy matching the app's selector. jQuery delegation cares
 * only about selector-match on the event path, so the decoy triggers the
 * handler, which then reads the REAL picker's checked state and swaps
 * model + disclaimer itself.
 *
 * NOT to be confused with the theme's own assets/3d-ring-viewer.js
 * (<ring-viewer-3d>, used by the ring-builder page) — that viewer is ours,
 * independent, and unaffected by this file.
 *
 * Safety notes, each verified against the code it concerns:
 * - Run-once guard: quick-add modals re-execute MainProduct section scripts
 *   (quick-add.js recreates script tags), so without the guard every modal
 *   open would add another permanent subscriber.
 * - The gate requires the app block OUTSIDE any quick-add modal, keeping
 *   the bridge PDP-only: a 3d-template product embedded in a quick-add on
 *   some other page must not trigger cross-product model reads.
 * - If a click-outside-closable modal (header search) is open when the
 *   variant fetch resolves, the poke is deferred until it closes — a
 *   body-bubbling click would otherwise snap it shut mid-typing.
 *   ⚠ The defer guard covers <details-modal> only. cart-notification has
 *   the same body-click closer but is inert while settings cart_type is
 *   "page" (its current value). If the cart is ever switched to the
 *   notification style, add it to the guard or an open cart popup will be
 *   closed by the poke after a variant change.
 * - The decoy is a nameless <button type="button">. The theme's legacy
 *   readers of `.single-option-selector` (product-variant-options.liquid)
 *   ignore it: the option collector keeps only checked radios/selects, and
 *   its change listener requires `target.name`. We dispatch click, not
 *   change, regardless.
 * - Known cosmetic side effect: a keyboard-driven variant change now closes
 *   open info tooltips / collapsible nav the same way a mouse click always
 *   did (the synthetic click bubbles into their outside-click closers).
 * - Known limit, inherited from the theme: variantChange is only published
 *   when the chosen options resolve to a variant record. A combination
 *   with NO matching variant publishes nothing, so the model would stay —
 *   but sold-out variants that exist as records still publish and swap
 *   correctly (live-verified), and every 3D product currently carries a
 *   full variant matrix, so today this path is unreachable.
 *
 * If the app is ever updated to watch the current markup, delete this file
 * and its script tag in sections/main-product.liquid; nothing else
 * references it.
 */
(() => {
  if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
  if (window.__jh3dVariantBridge) return;
  window.__jh3dVariantBridge = true;

  const APP_BLOCK = '.shopify-app-block[id*="3d_ring_viewer"]';
  let decoy = null;

  const poke = () => {
    const block = document.querySelector(APP_BLOCK);
    if (!block || block.closest('quick-add-modal')) return;

    const openModal = document.querySelector('details-modal details[open]');
    if (openModal) {
      openModal.addEventListener('toggle', poke, { once: true });
      return;
    }

    if (!decoy || !decoy.isConnected) {
      decoy = document.createElement('div');
      decoy.className = 'product-form__item';
      decoy.hidden = true;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'single-option-selector';
      btn.tabIndex = -1;
      btn.setAttribute('aria-hidden', 'true');
      decoy.appendChild(btn);
      document.body.appendChild(decoy);
    }

    decoy.firstElementChild.dispatchEvent(new MouseEvent('click', { bubbles: true }));
  };

  subscribe(PUB_SUB_EVENTS.variantChange, poke);
})();
